import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import HierarchicalOpalGrid from '../DenaliDataPipeline/report/HierarchicalOpalGrid'
import FlexColumn from '../../molecules/FlexColumn'
import useBlueprintData from './hooks/useBlueprintData'
import { LATCH_LATEST } from './constants'
import useBlueprintColumnDefs, { mapDefaultSort } from './hooks/useBlueprintColumnDefs'
import useBlueprintTreeData from './hooks/useBlueprintTreeData'
import BlueprintExportButton from './BlueprintExportButton'

function getSubRows (row) {
  return row.subRows
}

/**
 * Blueprint-driven hierarchical table. Fetches flat data from a single purpose,
 * transforms it into a tree via composite-key grouping, and renders a read-only
 * expandable table. Supports three aggregation modes: none, client, and server.
 */
function BlueprintHierarchicalTable ({
  blueprintCode,
  config,
  latch,
  displayName,
  parameters
}) {
  const {
    purposeCode,
    footerPurposeCode,
    footerColumnMapping,
    defaultSort: rawDefaultSort,
    hierarchy
  } = config

  const effectiveParameters = useMemo(() => {
    if (!latch) return parameters
    return { ...parameters, [latch.parameterName]: LATCH_LATEST }
  }, [parameters, latch])

  const hasFooterPurpose = !!footerPurposeCode && footerPurposeCode !== purposeCode

  const { data: mainData, isLoading: mainLoading } = useBlueprintData(
    blueprintCode,
    purposeCode,
    { parameters: effectiveParameters, pageSize: 1000 }
  )

  const { data: footerData, isLoading: footerLoading } = useBlueprintData(
    blueprintCode,
    hasFooterPurpose ? footerPurposeCode : null,
    { parameters: effectiveParameters, pageSize: 1000 }
  )

  const { columns, includeFooter } = useBlueprintColumnDefs(
    config.columns,
    hasFooterPurpose ? footerData : [],
    footerColumnMapping
  )

  const defaultSort = useMemo(() => mapDefaultSort(rawDefaultSort), [rawDefaultSort])

  const visibleColumns = useMemo(() => {
    return columns.filter(col => col.defaultShown ?? true)
  }, [columns])

  const { treeData, expandState, setExpandState } = useBlueprintTreeData(mainData, hierarchy)

  const isLoading = mainLoading || (hasFooterPurpose && footerLoading)
  const showToolbar = !!config.export?.enabled

  const table = (
    <HierarchicalOpalGrid
      data={treeData}
      columns={visibleColumns}
      isFetching={isLoading}
      pageState={{ pageIndex: 0, pageSize: 10000 }}
      onPageChange={() => {}}
      expandState={expandState}
      onExpandStateChange={setExpandState}
      sortState={defaultSort}
      onSortChange={() => {}}
      getSubRows={getSubRows}
      includeFooter={includeFooter}
    />
  )

  if (!showToolbar && !displayName) return table

  return (
    <FlexColumn className='gap-xs'>
      <div className='flex flex-row justify-between items-end'>
        <div className='flex flex-col gap-xs'>
          {displayName
            ? <div className='opal-heading opal-heading-2 font-size-lg'>{displayName}</div>
            : null}
        </div>
        {showToolbar
          ? (
            <div className='flex flex-row gap-sm'>
              <BlueprintExportButton
                blueprintCode={blueprintCode}
                config={config}
                purposeCode={purposeCode}
                parameters={effectiveParameters}
              />
            </div>
          )
          : null}
      </div>
      {table}
    </FlexColumn>
  )
}

BlueprintHierarchicalTable.propTypes = {
  blueprintCode: PropTypes.string.isRequired,
  config: PropTypes.shape({
    purposeCode: PropTypes.string.isRequired,
    columns: PropTypes.arrayOf(PropTypes.shape({
      name: PropTypes.string.isRequired,
      displayName: PropTypes.string,
      hidden: PropTypes.bool,
      exclude: PropTypes.bool,
      sortable: PropTypes.bool,
      filterable: PropTypes.bool,
      formatString: PropTypes.string
    })),
    hierarchy: PropTypes.shape({
      levels: PropTypes.arrayOf(PropTypes.shape({
        groupBy: PropTypes.arrayOf(PropTypes.string).isRequired,
        labelAccessor: PropTypes.string.isRequired
      })).isRequired,
      defaultExpandDepth: PropTypes.number,
      aggregation: PropTypes.shape({
        mode: PropTypes.oneOf(['none', 'client', 'server']),
        columnAggregates: PropTypes.arrayOf(PropTypes.shape({
          column: PropTypes.string.isRequired,
          fn: PropTypes.string.isRequired
        })),
        depthColumn: PropTypes.string
      })
    }).isRequired,
    footerPurposeCode: PropTypes.string,
    footerColumnMapping: PropTypes.arrayOf(PropTypes.shape({
      mainColumn: PropTypes.string,
      footerColumn: PropTypes.string
    })),
    defaultSort: PropTypes.arrayOf(PropTypes.shape({
      field: PropTypes.string,
      dir: PropTypes.string
    })),
    export: PropTypes.shape({
      enabled: PropTypes.bool,
      formats: PropTypes.arrayOf(PropTypes.string),
      defaultFileName: PropTypes.string
    })
  }).isRequired,
  latch: PropTypes.object,
  displayName: PropTypes.string,
  parameters: PropTypes.object
}

BlueprintHierarchicalTable.defaultProps = {
  latch: null,
  parameters: {}
}

export default BlueprintHierarchicalTable
